import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { DateTime } from 'luxon'
import { useBooleanFlagValue } from '@openfeature/react-sdk'
import { faPlus } from '@fortawesome/pro-regular-svg-icons'
import { Alert, Button, Checkbox, Icon, Input, Loader, Sheet, Text } from '@/ui'
import { mtc, TypographyStyles } from '@/styles'
import { centsToCurrency } from '@/utils/currencyHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { useHasContentBelow } from './lib/useHasContentBelow'
import { useAppDispatch } from '@/utils/typeHelpers'
import {
  getFetchError,
  resolveFetch,
  selectIsFetchingForKeys,
} from '@/reducers/fetch'
import { ProductFeatureId } from '@/actions/permissionActions'
import { FEATURE_FLAG_KEYS } from '@/features/open-feature'
import { usePaymentMethodUpdate } from '@/features/billing/payment-method/usePaymentMethodUpdate'
import type { PromoSource } from './analytics/plan-change-analytics.constants'
import { usePlanChangeAnalytics } from './analytics/use-plan-change-analytics'
import { PriceComparison } from './PriceComparison'
import { SupportLink } from './SupportLink'
import {
  FailurePanel,
  PaymentFailurePanel,
  PaymentMethodFormPanel,
} from './PaymentFailurePanels'
import {
  acknowledgmentCopyForFeature,
  lossSentenceLabel,
} from './lib/entitlementCopy'
import { displayRowLabel, TAX_FILING_ROW_KEYS } from './lib/taxFilingRows'
import {
  CADENCE_SUFFIX,
  PLAN_CHANGE_STEP,
  useCatalogTierDisplayNames,
  useTierDisplayNames,
  type TierDisplayNames,
  appliedPromoCopy,
  confirmTitleFor,
  discountCarryCopy,
  moneyLineCopy,
  pendingChangeFinalCopy,
  promoSpanLabel,
  prorationBreakdownRows,
  reassuranceCopy,
  submitLabelFor,
  unofferedPendingTier,
  usePlanChangeNavigation,
} from './lib/helpers'
import {
  SUBMIT_PLAN_CHANGE_KEY,
  fetchPlanOptions,
  previewPromoCode,
  submitPlanChange,
} from './lib/planChange.slice'
import {
  selectPlanOptions,
  selectPlanOptionsFetchedAt,
} from './lib/planChange.selectors'
import {
  isPaymentFailureCode,
  isCouponScopeUnreadableCode,
  isPlanChangeFailureCode,
  isPromoCodeRejectionReason,
  type CatalogLookupKey,
  type CurrentPlan,
  type PlanChangeFailureCode,
  type PlanChangePaymentFailureCode,
  type PlanChangeUnavailableCode,
  type PlanChangeTarget,
  type PlanChangeTier,
  type PromoCodePreview,
  type PromoCodeRejectionReason,
} from './lib/planChange.types'

const promoSourceFor = (
  target: PlanChangeTarget,
  typedCode: string | undefined
): PromoSource => {
  if (typedCode) return 'typed'
  return target.promo ? 'automatic' : 'none'
}

const OPTIONS_STALE_AFTER_MS = 10 * 60 * 1000

export interface ConfirmStepProps {
  lookupKey: string
  isRecommended?: boolean
}

/**
 * Keys on `discountLabel` rather than comparing effective to list: the label is null for a
 * `once` coupon, which is spent on the next invoice and carries over nothing, and null for
 * a grandfathered rate, where the legacy price *is* list.
 */
const rateFootnoteFor = (
  currentPlan: CurrentPlan,
  target: PlanChangeTarget,
  names: TierDisplayNames
): string | null => {
  const targetListLabel = `${centsToCurrency(target.listAmountCents).format()}${CADENCE_SUFFIX[target.cadence]}`
  const carry = discountCarryCopy(
    currentPlan,
    target,
    DateTime.utc(),
    names[target.tier]
  )
  if (carry) {
    return `${carry}. List is ${targetListLabel}.`
  }
  if (target.promo) {
    // `once` is a single invoice, which is one billing period however long that period is.
    const { duration, durationInMonths } = target.promo
    if (duration === 'forever') {
      return null
    }
    const span = promoSpanLabel(
      duration === 'once' ? 1 : (durationInMonths ?? 1),
      target.cadence
    )
    return `The discounted price covers your first ${span}. After that it's ${targetListLabel}.`
  }
  return null
}

/**
 * A swap, not a loss: the business return stops being included and the personal return starts,
 * which the gain and confirm lists state half each and pages apart.
 */
const swapsTheFilings = (
  target: Pick<PlanChangeTarget, 'marketedGains' | 'lost'>
): boolean =>
  target.marketedGains.includes(TAX_FILING_ROW_KEYS.personalIncluded) &&
  target.lost.includes(ProductFeatureId.annualTaxesBusinessFiling)

/**
 * The `5xx` and no-status failure states, split by the `500` body's `errorCode`.
 * `rolled_back` is safe to retry and safe to say nothing was charged about.
 * `applied_unconfirmed` means Stripe took the change and only the bookkeeping after it
 * failed, so it offers no retry: the reconciler settles the row and a second attempt answers
 * `409`. Anything unclassified keeps outcome-unknown copy, since the client cannot tell from
 * the outside whether money moved.
 */
const SubmitFailurePanel = ({
  failureCode,
  onGoToBilling,
  onRetry,
}: {
  failureCode: PlanChangeFailureCode | undefined
  onGoToBilling: () => void
  onRetry: () => void
}) => {
  if (failureCode === 'rolled_back') {
    return (
      <FailurePanel
        title="Something went wrong on our end"
        actions={<Button onClick={onRetry}>Try again</Button>}
      >
        Nothing was charged and your plan hasn&rsquo;t changed. Try again, or
        email <SupportLink /> if it keeps happening.
      </FailurePanel>
    )
  }

  if (failureCode === 'applied_unconfirmed') {
    return (
      <FailurePanel
        title="Your plan change went through"
        actions={<Button onClick={onGoToBilling}>Go to billing</Button>}
      >
        We couldn&rsquo;t finish updating your account, so it may take a few
        minutes to show up. Don&rsquo;t confirm again. Your billing page has the
        current state, and <SupportLink /> can help if it still looks wrong in
        an hour.
      </FailurePanel>
    )
  }

  return (
    <FailurePanel
      title="Check your billing page"
      actions={
        <div className="flex gap-3">
          <Button onClick={onGoToBilling}>Go to billing</Button>
          <Button variant="secondary" onClick={onRetry}>
            Try again
          </Button>
        </div>
      }
    >
      Something went wrong after you confirmed and we can&rsquo;t tell whether
      the change went through. Your billing page shows the plan you&rsquo;re on
      now. Email <SupportLink /> if it doesn&rsquo;t look right.
    </FailurePanel>
  )
}

/**
 * A `409` means pricing or eligibility changed since the customer opened this screen. If the
 * refetch that follows it also fails, the confirm screen would otherwise sit there with a
 * live submit button that just answers `409` again, so this stands in for it.
 */
const ConflictRefetchFailedPanel = ({
  onRetry,
  onBackToPlans,
}: {
  onRetry: () => void
  onBackToPlans: () => void
}) => (
  <FailurePanel
    title="We couldn’t refresh your plan options"
    actions={
      <div className="flex gap-3">
        <Button onClick={onRetry}>Try again</Button>
        <Button variant="secondary" onClick={onBackToPlans}>
          Back to my plan options
        </Button>
      </div>
    }
  >
    Pricing or eligibility changed since you opened this, and we couldn&rsquo;t
    load the update. Nothing was charged. Try again, or email <SupportLink /> if
    it keeps happening.
  </FailurePanel>
)

const CouponScopeUnreadablePanel = ({
  message,
  onBackToPlans,
}: {
  message: string
  onBackToPlans: () => void
}) => (
  <FailurePanel
    title="We couldn’t check your discount"
    actions={<Button onClick={onBackToPlans}>Back to plans</Button>}
  >
    {message} Nothing was charged. Email <SupportLink /> if it keeps happening.
  </FailurePanel>
)

type PromoCodeCheck =
  | { state: 'idle' }
  | { state: 'checking' }
  | { state: 'result'; preview: PromoCodePreview }
  | { state: 'check_failed' }

interface Consent {
  checkedIds: Set<string>
  reviewPrompted: boolean
  promoCode: string
  promoCheck: PromoCodeCheck
}

const freshConsent = (): Consent => ({
  checkedIds: new Set(),
  reviewPrompted: false,
  promoCode: '',
  promoCheck: { state: 'idle' },
})

type PromoFieldPatch = Partial<{
  promoCode: string
  promoCheck: PromoCodeCheck
}>

const promoCheckMessage = (
  check: PromoCodeCheck
): { text: string; tone: 'green' | 'red' } | null => {
  if (check.state === 'result' && check.preview.status === 'valid') {
    return { text: appliedPromoCopy(check.preview.label), tone: 'green' }
  }
  if (check.state === 'result' && check.preview.status === 'rejected') {
    return { text: check.preview.message, tone: 'red' }
  }
  if (check.state === 'check_failed') {
    return { text: 'Couldn’t check that code. Try again.', tone: 'red' }
  }
  return null
}

const PromoCodeField = ({
  lookupKey,
  code,
  check,
  onChange,
  onClearPromoRejection,
}: {
  lookupKey: CatalogLookupKey
  code: string
  check: PromoCodeCheck
  onChange: (patch: PromoFieldPatch) => void
  onClearPromoRejection: () => void
}) => {
  const dispatch = useAppDispatch()
  const messageId = useId()
  const {
    trackPromoCodeEntered,
    trackPromoCodeAccepted,
    trackPromoCodeRejected,
  } = usePlanChangeAnalytics()

  const handleApply = async () => {
    const trimmed = code.trim()
    if (!trimmed || check.state === 'checking') return
    onClearPromoRejection()
    trackPromoCodeEntered()
    onChange({ promoCheck: { state: 'checking' } })
    const preview = await previewPromoCode({
      code: trimmed,
      toLookupKey: lookupKey,
    })(dispatch)
    if (!preview) {
      onChange({ promoCheck: { state: 'check_failed' } })
      return
    }
    if (preview.status === 'valid') {
      trackPromoCodeAccepted()
    } else {
      trackPromoCodeRejected({ reason: preview.reason, stage: 'preview' })
    }
    onChange({ promoCheck: { state: 'result', preview } })
  }

  const handleChange = (value: string) => {
    if (!value.trim()) onClearPromoRejection()
    onChange({
      promoCode: value,
      ...(check.state !== 'idle' && { promoCheck: { state: 'idle' } }),
    })
  }

  const handleRemove = () => {
    onClearPromoRejection()
    onChange({ promoCode: '', promoCheck: { state: 'idle' } })
  }

  const isValid = check.state === 'result' && check.preview.status === 'valid'
  const isRejected =
    check.state === 'result' && check.preview.status === 'rejected'
  const message = promoCheckMessage(check)

  return (
    <div className="flex flex-col gap-2">
      <form
        className="flex gap-2"
        onSubmit={(event) => {
          event.preventDefault()
          void handleApply()
        }}
      >
        <Input
          value={code}
          onChange={(event) => handleChange(event.target.value)}
          placeholder="Promo code"
          aria-label="Promo code"
          aria-describedby={message ? messageId : undefined}
          aria-invalid={isRejected || undefined}
          disabled={check.state === 'checking' || isValid}
          error={isRejected}
        />
        {isValid ? (
          <Button type="button" variant="secondary" onClick={handleRemove}>
            Remove
          </Button>
        ) : (
          <Button
            type="submit"
            variant="secondary"
            disabled={!code.trim() || check.state === 'checking'}
          >
            {check.state === 'checking' ? 'Checking…' : 'Apply'}
          </Button>
        )}
      </form>
      {message && (
        <Text
          id={messageId}
          role={message.tone === 'red' ? 'alert' : 'status'}
          variant={TypographyStyles.BODY_SM}
          className={
            message.tone === 'green' ? 'text-green-100' : 'text-red-100'
          }
        >
          {message.text}
        </Text>
      )}
    </div>
  )
}

interface ConfirmSheetBodyProps {
  currentPlan: CurrentPlan
  target: PlanChangeTarget
  hasPendingChange: boolean
  finalPendingTier: PlanChangeTier | null
  isSubmitting: boolean
  submitStatus: number | undefined
  promoCodeRejection: PromoCodeRejectionReason | undefined
  promoCodeRejectionMessage: string | undefined
  onConfirm: (acknowledgedLosses: string[], promoCode?: string) => Promise<void>
  onDismiss: () => void
  onClearPromoRejection: () => void
  consent: Consent
  onConsentChange: (change: (current: Consent) => Partial<Consent>) => void
}

const ConfirmSheetBody = ({
  currentPlan,
  target,
  hasPendingChange,
  finalPendingTier,
  isSubmitting,
  submitStatus,
  promoCodeRejection,
  promoCodeRejectionMessage,
  onConfirm,
  onDismiss,
  onClearPromoRejection,
  consent: { checkedIds, reviewPrompted, promoCode, promoCheck },
  onConsentChange,
}: ConfirmSheetBodyProps) => {
  const names = useTierDisplayNames()
  const catalogNames = useCatalogTierDisplayNames()
  const reassurance = reassuranceCopy(target, currentPlan)
  const promoCodesEnabled = useBooleanFlagValue(
    FEATURE_FLAG_KEYS.planChangePromoCodes,
    false
  )
  // An automatic offer never stacks with a typed code, so the field hides beside one.
  const showsPromoCodeField =
    promoCodesEnabled && target.timing === 'immediate' && !target.promo
  const appliedPreview =
    showsPromoCodeField &&
    promoCheck.state === 'result' &&
    promoCheck.preview.status === 'valid'
      ? promoCheck.preview
      : undefined
  const appliedPromoCode = appliedPreview ? promoCode.trim() : undefined
  // Display only: the field's visibility and the automatic-offer line must read the raw target.
  const displayTarget: PlanChangeTarget = appliedPreview
    ? {
        ...target,
        prorationAmountCents: appliedPreview.prorationAmountCents,
        prorationCreditCents: appliedPreview.prorationCreditCents,
        prorationChargeCents: appliedPreview.prorationChargeCents,
        promo: {
          label: appliedPreview.label,
          discountedAmountCents: appliedPreview.discountedAmountCents,
          duration: appliedPreview.duration,
          durationInMonths: appliedPreview.durationInMonths,
        },
      }
    : target
  const breakdownRows = prorationBreakdownRows(
    displayTarget,
    currentPlan,
    names,
    catalogNames
  )

  useEffect(() => {
    if (promoCodeRejection) {
      onConsentChange(() => ({ promoCheck: { state: 'idle' } }))
    }
  }, [promoCodeRejection, onConsentChange])

  const toggleAcknowledgment = (featureId: string, isChecked: boolean): void =>
    onConsentChange((current) => {
      const next = new Set(current.checkedIds)
      if (isChecked) {
        next.add(featureId)
      } else {
        next.delete(featureId)
      }
      return { checkedIds: next }
    })

  const setReviewPrompted = (value: boolean): void =>
    onConsentChange(() => ({ reviewPrompted: value }))

  const onPromoFieldChange = (patch: PromoFieldPatch): void =>
    onConsentChange(() => patch)

  const [bodyElement, setBodyElement] = useState<HTMLDivElement | null>(null)
  const hasContentBelow = useHasContentBelow(bodyElement)

  const handleConfirm = async () => {
    if (isSubmitting || promoCheck.state === 'checking') return
    if (target.lost.some((featureId) => !checkedIds.has(featureId))) {
      setReviewPrompted(true)
      return
    }
    setReviewPrompted(false)
    await onConfirm([...checkedIds], appliedPromoCode)
  }

  return (
    <>
      <div
        ref={setBodyElement}
        className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-8 py-6 [&>*]:shrink-0"
      >
        <div className="rounded-[18px] border border-moss-50 bg-moss-0 p-[22px]">
          <Text variant={TypographyStyles.LABEL_MD}>
            {moneyLineCopy(displayTarget, currentPlan)}
          </Text>
          {breakdownRows.length > 0 && (
            <div className="mt-3 flex flex-col gap-1">
              {breakdownRows.map(({ label, amount }) => (
                <div
                  key={label}
                  className="flex items-baseline justify-between gap-4"
                >
                  <Text
                    variant={TypographyStyles.BODY_SM}
                    className="text-neutral-600"
                  >
                    {label}
                  </Text>
                  <Text variant={TypographyStyles.BODY_SM}>{amount}</Text>
                </div>
              ))}
            </div>
          )}
          {currentPlan.isTrialing && (
            <Text
              variant={TypographyStyles.BODY_SM}
              className="mt-2 text-neutral-500"
            >
              This ends your free trial today.
            </Text>
          )}
          {target.timing === 'immediate' && (
            <Text
              variant={TypographyStyles.BODY_XS}
              className="mt-2 text-neutral-500"
            >
              Amounts shown today are estimates. Your invoice shows the final
              amount.
            </Text>
          )}
          {target.promo && (
            <Text
              variant={TypographyStyles.BODY_SM}
              className="mt-2 text-green-100"
            >
              {appliedPromoCopy(target.promo.label)}
            </Text>
          )}
        </div>

        {showsPromoCodeField && (
          <PromoCodeField
            lookupKey={target.lookupKey}
            code={promoCode}
            check={promoCheck}
            onChange={onPromoFieldChange}
            onClearPromoRejection={onClearPromoRejection}
          />
        )}

        <PriceComparison
          currentPlan={currentPlan}
          target={displayTarget}
          footnote={rateFootnoteFor(currentPlan, displayTarget, catalogNames)}
        />

        {swapsTheFilings(target) && (
          <div className="flex flex-col gap-3">
            <Text variant={TypographyStyles.DISPLAY_XS}>What changes</Text>
            <Text variant={TypographyStyles.BODY_MD}>
              Your business return: included today, an added cost on{' '}
              {catalogNames[target.tier]}. Your personal return: an added cost
              today, included on {catalogNames[target.tier]}.
            </Text>
          </div>
        )}

        {target.marketedGains.length > 0 && (
          <div className="flex flex-col gap-3">
            <Text
              variant={TypographyStyles.EYEBROW}
              className="text-neutral-500"
            >
              What you&rsquo;re gaining
            </Text>
            <ul className="flex flex-col gap-2">
              {target.marketedGains.map((featureId) => (
                <li key={featureId} className="flex items-start gap-2">
                  <Icon
                    icon={faPlus}
                    color="text-green-100"
                    className="mt-1 text-sm"
                  />
                  <Text variant={TypographyStyles.BODY_MD}>
                    {displayRowLabel(featureId)}
                  </Text>
                </li>
              ))}
            </ul>
          </div>
        )}

        {target.lost.length > 0 && (
          <div className="flex flex-col gap-3">
            <Text variant={TypographyStyles.DISPLAY_XS}>
              {target.lost.length === 1
                ? 'One thing to confirm'
                : `${target.lost.length} things to confirm`}
            </Text>
            {target.lost.map((featureId) => {
              const isChecked = checkedIds.has(featureId)
              const featureLabel = lossSentenceLabel(featureId)
              const consequenceCopy = acknowledgmentCopyForFeature(featureId)
              return (
                <div
                  key={featureId}
                  className={mtc(
                    'rounded-[14px] border p-4',
                    isChecked
                      ? 'border-moss-50 bg-moss-0'
                      : 'border-neutral-100 bg-neutral-0'
                  )}
                >
                  <Checkbox
                    // The atom derives its id and aria-label from `label` only when that
                    // is a string. A node label leaves every box sharing
                    // `checkbox-undefined`, so a label click lands on the first one. The
                    // atom also uses `ariaLabel` as the entire accessible name for a node
                    // label, so it must carry the consequence copy, not just the feature.
                    id={`ack-${featureId}`}
                    ariaLabel={`You'll be giving up ${featureLabel}. ${consequenceCopy}`}
                    checked={isChecked}
                    onCheckedChange={(checked) =>
                      toggleAcknowledgment(featureId, checked)
                    }
                    label={
                      <span className="flex flex-col gap-1">
                        <Text variant={TypographyStyles.LABEL_MD}>
                          {`You'll be giving up ${featureLabel}`}
                        </Text>
                        <Text
                          variant={TypographyStyles.BODY_SM}
                          className="text-neutral-600"
                        >
                          {consequenceCopy}
                        </Text>
                      </span>
                    }
                  />
                </div>
              )
            })}
          </div>
        )}

        {hasPendingChange && (
          <Alert variant="warning">
            This replaces the plan change you already have scheduled.
            {finalPendingTier &&
              ` ${pendingChangeFinalCopy(finalPendingTier, catalogNames)}`}
          </Alert>
        )}

        {submitStatus === 403 && (
          <Alert variant="error">
            Plan changes aren&apos;t available while impersonating a customer.
          </Alert>
        )}

        {promoCodeRejection && (
          <Alert variant="error">{promoCodeRejectionMessage}</Alert>
        )}

        {reassurance && (
          <Text variant={TypographyStyles.BODY_SM} className="text-neutral-500">
            {reassurance}
          </Text>
        )}
      </div>

      <div
        className={mtc(
          'flex shrink-0 flex-col gap-3 bg-neutral-0 px-8 py-5',
          hasContentBelow &&
            'shadow-[0_-2px_4px_-1px_rgba(0,0,0,0.06),0_-4px_6px_-1px_rgba(0,0,0,0.10)]'
        )}
      >
        {reviewPrompted && (
          <Text
            role="alert"
            variant={TypographyStyles.BODY_SM}
            className="text-yellow-300"
          >
            Please review and check each item above to continue.
          </Text>
        )}
        <div className="flex gap-3">
          <Button
            className="flex-1"
            disabled={
              isSubmitting ||
              submitStatus === 403 ||
              promoCheck.state === 'checking'
            }
            onClick={handleConfirm}
          >
            {isSubmitting ? 'Working on it…' : submitLabelFor(displayTarget)}
          </Button>
          <Button
            variant="secondary"
            disabled={isSubmitting}
            onClick={onDismiss}
          >
            Not now
          </Button>
        </div>
      </div>
    </>
  )
}

export const ConfirmStep = ({
  lookupKey,
  isRecommended = false,
}: ConfirmStepProps) => {
  const names = useTierDisplayNames()
  const catalogNames = useCatalogTierDisplayNames()
  const dispatch = useAppDispatch()
  const { goToStep, resetFlow } = usePlanChangeNavigation()
  const {
    trackConfirmViewed,
    trackSubmitted,
    trackFailed,
    trackPromoCodeRejected,
  } = usePlanChangeAnalytics()
  const confirmViewedTrackedRef = useRef(false)
  const planOptions = useReselector(selectPlanOptions)
  const fetchedAt = useReselector(selectPlanOptionsFetchedAt)
  const submitError = useReselector(getFetchError, SUBMIT_PLAN_CHANGE_KEY)
  const isSubmitting = useReselector(selectIsFetchingForKeys, [
    SUBMIT_PLAN_CHANGE_KEY,
  ])
  const clearSubmitError = () => {
    dispatch(resolveFetch(SUBMIT_PLAN_CHANGE_KEY))
  }
  const [cardSavedAnnouncement, setCardSavedAnnouncement] = useState('')
  const paymentMethodUpdate = usePaymentMethodUpdate({
    surface: 'decline sheet',
    onSaved: () => {
      clearSubmitError()
      setCardSavedAnnouncement(
        'Card saved. Confirm your plan change to finish.'
      )
    },
  })
  const formOpen =
    paymentMethodUpdate.status !== 'idle' &&
    paymentMethodUpdate.status !== 'saved'
  const sheetLocked = isSubmitting || paymentMethodUpdate.status === 'saving'
  const [refetching, setRefetching] = useState(
    () =>
      planOptions === null ||
      fetchedAt === null ||
      Date.now() - fetchedAt > OPTIONS_STALE_AFTER_MS
  )
  const [conflictRefetchFailed, setConflictRefetchFailed] = useState(false)
  const [staleRefetchFailed, setStaleRefetchFailed] = useState(false)
  const [consentState, setConsentState] = useState<{
    identity: string | null
    consent: Consent
  }>({ identity: null, consent: freshConsent() })

  const currentPlan = planOptions?.currentPlan ?? null
  const target = planOptions?.targets.find(
    (candidate) => candidate.lookupKey === lookupKey
  )
  const navigate = useNavigate()
  const submitStatus = submitError?.statusCode
  // A submit that failed with no status at all (network drop, axios timeout) is the same
  // problem as a 5xx: the client cannot say whether the change landed.
  const outcomeUnconfirmed =
    submitError !== undefined &&
    (submitStatus === undefined || submitStatus >= 500)
  const submitPaymentFailureCode: PlanChangePaymentFailureCode | undefined =
    isPaymentFailureCode(submitError?.errorCode)
      ? submitError.errorCode
      : undefined
  const submitFailureCode: PlanChangeFailureCode | undefined =
    isPlanChangeFailureCode(submitError?.errorCode)
      ? submitError.errorCode
      : undefined
  const unavailableCode: PlanChangeUnavailableCode | undefined =
    submitStatus === 409 && isCouponScopeUnreadableCode(submitError?.errorCode)
      ? submitError.errorCode
      : undefined
  const couponScopeUnreadable = unavailableCode !== undefined
  // Confirm re-resolves the code, so a code the preview accepted can still come back 422.
  const promoCodeRejection = isPromoCodeRejectionReason(submitError?.errorCode)
    ? submitError.errorCode
    : undefined
  const promoCodeRejectionMessage = promoCodeRejection
    ? submitError?.message
    : undefined
  // Editing the code must not dismiss an unrelated submit error, such as the impersonation 403.
  const clearPromoRejection = (): void => {
    if (promoCodeRejection) clearSubmitError()
  }
  // Consent must die exactly when the consent question changes, and survive a
  // refetch that returns the same target with the same losses.
  const targetIdentity = target
    ? `${target.lookupKey}:${[...target.lost].sort().join(',')}`
    : null

  const consent: Consent =
    consentState.identity === targetIdentity
      ? consentState.consent
      : freshConsent()

  // Functional so an async promo check can't overwrite a checkbox ticked while it was pending.
  const onConsentChange = useCallback(
    (change: (current: Consent) => Partial<Consent>): void =>
      setConsentState((prev) => {
        const current =
          prev.identity === targetIdentity ? prev.consent : freshConsent()
        return {
          identity: targetIdentity,
          consent: { ...current, ...change(current) },
        }
      }),
    [targetIdentity]
  )

  useEffect(() => {
    let mounted = true
    if (refetching) {
      fetchPlanOptions()(dispatch).then((options) => {
        if (!mounted) return
        // A failed staleness refetch leaves planOptions null, and the guard below returns
        // early on null, so without this the screen sits on a loader with no way out.
        if (!options) setStaleRefetchFailed(true)
        setRefetching(false)
      })
    }
    return () => {
      mounted = false
    }
    // Mount only: this is the staleness check itself, so re-running it on a
    // planOptions change would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (refetching || !planOptions) return
    if (!target || !currentPlan) resetFlow()
  }, [refetching, planOptions, target, currentPlan, resetFlow])

  const retryStaleRefetch = () => {
    setStaleRefetchFailed(false)
    setRefetching(true)
    fetchPlanOptions()(dispatch).then((options) => {
      if (!options) setStaleRefetchFailed(true)
      setRefetching(false)
    })
  }

  const attemptConflictRefetch = () => {
    setConflictRefetchFailed(false)
    fetchPlanOptions()(dispatch).then((res) => {
      if (res) {
        resetFlow({ conflict: true })
      } else {
        setConflictRefetchFailed(true)
      }
    })
  }

  useEffect(() => {
    if (
      confirmViewedTrackedRef.current ||
      refetching ||
      !target ||
      !currentPlan
    )
      return
    confirmViewedTrackedRef.current = true
    trackConfirmViewed({
      fromTier: currentPlan.tier,
      fromCadence: currentPlan.cadence,
      toTier: target.tier,
      toCadence: target.cadence,
      isRecommended,
      timing: target.timing,
      endingTrial: currentPlan.isTrialing,
      lossCount: target.lost.length,
      promoSource: promoSourceFor(target, undefined),
    })
  }, [target, currentPlan, refetching, trackConfirmViewed, isRecommended])

  useEffect(() => {
    if (!submitError) return
    trackFailed({
      status: submitError.statusCode ?? null,
      errorCode:
        submitPaymentFailureCode ??
        submitFailureCode ??
        unavailableCode ??
        promoCodeRejection ??
        null,
    })
    if (promoCodeRejection) {
      trackPromoCodeRejected({ reason: promoCodeRejection, stage: 'confirm' })
    }
  }, [
    submitError,
    submitPaymentFailureCode,
    submitFailureCode,
    unavailableCode,
    promoCodeRejection,
    trackFailed,
    trackPromoCodeRejected,
  ])

  useEffect(() => {
    if (submitStatus !== 409 || couponScopeUnreadable) return
    attemptConflictRefetch()
    // attemptConflictRefetch is re-created every render, so depending on it would
    // refetch on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitStatus, couponScopeUnreadable, dispatch, resetFlow])

  useEffect(
    () => () => {
      if (targetIdentity) dispatch(resolveFetch(SUBMIT_PLAN_CHANGE_KEY))
    },
    [targetIdentity, dispatch]
  )

  const confirmChange = async (
    acknowledgedLosses: string[],
    promoCode?: string
  ) => {
    if (!target) return
    trackSubmitted({
      toTier: target.tier,
      toCadence: target.cadence,
      timing: target.timing,
      promoSource: promoSourceFor(target, promoCode),
    })
    const result = await submitPlanChange({
      toLookupKey: target.lookupKey,
      acknowledgedLosses,
      promoCode,
    })(dispatch)
    if (result) goToStep(PLAN_CHANGE_STEP.result)
  }

  if (submitStatus === 400 || submitStatus === 404) {
    return <Navigate replace to="/accounts" />
  }

  const isReady =
    !refetching &&
    planOptions !== null &&
    Boolean(target) &&
    Boolean(currentPlan)

  const paymentFailureContent = formOpen ? (
    <PaymentMethodFormPanel
      update={paymentMethodUpdate}
      failureCode={submitPaymentFailureCode}
    />
  ) : (
    <PaymentFailurePanel
      failureCode={submitPaymentFailureCode}
      onUpdatePaymentMethod={paymentMethodUpdate.open}
      onRetry={clearSubmitError}
      onDismiss={resetFlow}
    />
  )

  return (
    <Sheet
      open
      onOpenChange={(open) => {
        if (!open && !sheetLocked) resetFlow()
      }}
    >
      <Sheet.Content
        side="right"
        // The default scrim class, bg-black/80, has no color behind it in the theme.
        overlayClassName="bg-neutral-900/80"
        aria-describedby={undefined}
        dismissible={!sheetLocked}
        onEscapeKeyDown={(event) => {
          if (sheetLocked) event.preventDefault()
        }}
        onInteractOutside={(event) => {
          if (sheetLocked) event.preventDefault()
        }}
        className="flex flex-col gap-0 overflow-hidden p-0 sm:p-0 lg:w-[560px]"
      >
        <div className="flex shrink-0 flex-col gap-1 px-8 pt-8">
          <Text variant={TypographyStyles.EYEBROW} className="text-neutral-500">
            Review and confirm
          </Text>
          <Sheet.Title className="!mt-0">
            {isReady && target && currentPlan
              ? confirmTitleFor(
                  target,
                  currentPlan,
                  catalogNames[target.tier],
                  names
                )
              : 'Confirm your new plan'}
          </Sheet.Title>
        </div>

        <div role="status" className="sr-only">
          {cardSavedAnnouncement}
        </div>

        {submitStatus === 402 ? (
          paymentFailureContent
        ) : couponScopeUnreadable ? (
          <CouponScopeUnreadablePanel
            message={submitError?.message ?? ''}
            onBackToPlans={() => resetFlow()}
          />
        ) : submitStatus === 409 ? (
          conflictRefetchFailed ? (
            <ConflictRefetchFailedPanel
              onRetry={attemptConflictRefetch}
              onBackToPlans={() => resetFlow({ conflict: true })}
            />
          ) : (
            <div className="px-8 py-6">
              <Loader />
            </div>
          )
        ) : outcomeUnconfirmed ? (
          <SubmitFailurePanel
            failureCode={submitFailureCode}
            onGoToBilling={() => navigate('/accounts')}
            onRetry={clearSubmitError}
          />
        ) : staleRefetchFailed ? (
          <ConflictRefetchFailedPanel
            onRetry={retryStaleRefetch}
            onBackToPlans={() => resetFlow()}
          />
        ) : !isReady || !target || !currentPlan ? (
          <div className="px-8 py-6">
            <Loader />
          </div>
        ) : (
          <ConfirmSheetBody
            key={targetIdentity}
            currentPlan={currentPlan}
            target={target}
            hasPendingChange={Boolean(planOptions?.pendingChange)}
            finalPendingTier={unofferedPendingTier(
              planOptions?.pendingChange ?? null,
              planOptions?.targets ?? [],
              planOptions?.suppressedTargetCount ?? 0
            )}
            isSubmitting={isSubmitting}
            submitStatus={submitStatus}
            promoCodeRejection={promoCodeRejection}
            promoCodeRejectionMessage={promoCodeRejectionMessage}
            onConfirm={confirmChange}
            onDismiss={resetFlow}
            onClearPromoRejection={clearPromoRejection}
            consent={consent}
            onConsentChange={onConsentChange}
          />
        )}
      </Sheet.Content>
    </Sheet>
  )
}
